import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { QUERY_KEYS } from '~shared/api/query-keys';

export interface FilteredInvoiceRow {
  id: string;
  /** Legacy combined label; prefer parentName + studentName when present. */
  studentOrParentName: string;
  parentName?: string;
  studentName?: string;
  status: string;
  /**
   * Effective amount for the row (e.g. refund-aware); aligns with list filters / totals.
   * Kept for future use alongside `invoiceAmount` and `paidAmount`.
   */
  finalAmount: number;
  /** Stored invoice `finalAmount` on the document. */
  invoiceAmount?: number;
  /** Sum of `payments[].amount`. */
  paidAmount?: number;
  dueDate: string | null;
  /** Present when backend includes it (for payment-date filter). */
  paymentDate: string | null;
  parentId: string | null;
  studentId: string | null;
  createdAt: string;
  /** When Stripe will make funds available (for paid invoices). */
  fundsAvailableOn: string | null;
  /** Human-readable payment method(s), e.g. "Stripe", "Wallet", "Stripe, Wallet". */
  paymentMethodSummary: string;
  /** Amount paid via wallet for this invoice. */
  walletAmount: number;
  /** Sum of transaction-fee line items on the invoice. */
  transactionFeeAmount?: number;
  /** Individual payment records (wallet, card, etc.). */
  payments?: Array<{
    method: string;
    amount: number;
    date: string | null;
    paymentIntentId: string | null;
    checkNumber: string | null;
    cardLast4: string | null;
  }>;
  /** Stripe failure reason message when status is failed. */
  failureReason?: string | null;
  /** Stripe failure code/decline_code when available. */
  failureCode?: string | null;
  /** Class / event (or session fee) label from invoice line items. */
  className?: string;
  /**
   * Billing session id: `entityId` when type is session, else `session` on
   * the linked enrollment (class) or event.
   */
  sessionId?: string | null;
  /** Name from `sessions` for the resolved session id above. */
  sessionName?: string;
}

export type FilteredInvoicePaymentDetail = NonNullable<
  FilteredInvoiceRow['payments']
>[number];

export interface FilteredInvoicesPagination {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
}

export interface GetFilteredInvoicesResponse {
  totalAmount: number;
  /** Sum of amounts paid through wallet for all invoices matching current filters. */
  totalWalletAmount: number;
  invoices: Array<FilteredInvoiceRow>;
  pagination: FilteredInvoicesPagination;
}

/** Apply date range to "dueDate" or "paymentDate". Default "dueDate". */
export type InvoiceDateFilterField = 'dueDate' | 'paymentDate';

/** Must match backend `FilteredInvoicesQueryDto.sortBy`. */
export type FilteredInvoicesSortBy =
  | 'dueDate'
  | 'paymentDate'
  | 'parentName'
  | 'studentName'
  | 'className'
  | 'sessionName';

export type FilteredInvoicesSortOrder = 'asc' | 'desc';

export interface GetFilteredInvoicesParams {
  startDate?: string;
  endDate?: string;
  /** Apply date range to this field. Default "dueDate". */
  dateField?: InvoiceDateFilterField;
  /** Multiple statuses e.g. ['paid', 'pending'] */
  statuses?: Array<string>;
  finalAmount?: number;
  page?: number;
  limit?: number;
  sortBy?: FilteredInvoicesSortBy;
  sortOrder?: FilteredInvoicesSortOrder;
  /** Partial match on student name (linked student or invoice metadata). */
  studentSearch?: string;
}

const buildInvoiceFilterSearchParams = (params: GetFilteredInvoicesParams) => {
  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.set('startDate', params.startDate);
  if (params.endDate) searchParams.set('endDate', params.endDate);
  if (params.dateField) searchParams.set('dateField', params.dateField);
  if (params.statuses?.length)
    searchParams.set('statuses', params.statuses.join(','));
  if (params.finalAmount != null)
    searchParams.set('finalAmount', String(params.finalAmount));
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);
  if (params.studentSearch?.trim())
    searchParams.set('studentSearch', params.studentSearch.trim());
  return searchParams;
};

export const getFilteredInvoicesAPI = async (
  params?: GetFilteredInvoicesParams,
) => {
  const searchParams = buildInvoiceFilterSearchParams(params ?? {});
  if (params?.page != null) searchParams.set('page', String(params.page));
  if (params?.limit != null) searchParams.set('limit', String(params.limit));
  const query = searchParams.toString();
  const url = query
    ? `subscription-invoices/filtered?${query}`
    : 'subscription-invoices/filtered';
  const response = await baseAPI.get<GetFilteredInvoicesResponse>(url);
  return response.data;
};

export const useGetFilteredInvoices = (params?: GetFilteredInvoicesParams) => {
  return useQuery({
    queryKey: QUERY_KEYS.FILTERED_INVOICES(params),
    queryFn: () => getFilteredInvoicesAPI(params),
    placeholderData: keepPreviousData,
  });
};

/** Params for CSV export (filters only, no pagination) */
export interface DownloadFilteredInvoicesCSVParams {
  startDate?: string;
  endDate?: string;
  dateField?: InvoiceDateFilterField;
  statuses?: Array<string>;
  finalAmount?: number;
  studentSearch?: string;
  sortBy?: FilteredInvoicesSortBy;
  sortOrder?: FilteredInvoicesSortOrder;
}

export const downloadFilteredInvoicesCSVAPI = async (
  params?: DownloadFilteredInvoicesCSVParams,
): Promise<Blob> => {
  const searchParams = buildInvoiceFilterSearchParams(params ?? {});
  searchParams.set('csv', 'true');
  const query = searchParams.toString();
  const url = `subscription-invoices/filtered?${query}`;
  const response = await baseAPI.get<Blob>(url, {
    responseType: 'blob',
  });
  return response.data;
};
